import React, { useState, useEffect, useCallback } from "react";
import Select from "react-select";
import { debounce } from "lodash";
import { Eye, Edit3, Trash, ChevronDown, ChevronUp, Plus } from "lucide-react";
import DataTable from "../../../common/components/DataTable";
import {
  fetchUsers,
  fetchAllUsers,
  fetchRoles,
  fetchBranches,
  deleteUser,
} from "../../../common/api/userService";
import AddEditUserModal from "../components/AddEditUserModal"; // ✅ Import modal

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState(null);
  const [branchFilter, setBranchFilter] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState({});
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  // ✅ Debounced API call for fetching users
  const loadData = useCallback(
    debounce(async () => {
      setLoading(true);
      try {
        const usersData =
          searchQuery.trim() === ""
            ? await fetchAllUsers()
            : await fetchUsers({ search: searchQuery });

        const [rolesData, branchesData] = await Promise.all([fetchRoles(), fetchBranches()]);

        setUsers(usersData || []);
        setRoles(
          rolesData.map((role) => ({ value: role.role_id, label: role.name })) || []
        );
        setBranches(
          branchesData.map((branch) => ({ value: branch.branch_id, label: branch.name })) || []
        );
      } catch (error) {
        console.error("Failed to load data:", error);
      } finally {
        setLoading(false);
      }
    }, 500), // ✅ 500ms debounce time
    [searchQuery]
  );

  // ✅ Fetch data when filters or search query change
  useEffect(() => {
    loadData();
  }, [searchQuery, roleFilter, branchFilter, loadData]);

  // ✅ Handle user deletion
  const handleDelete = async (userId) => {
    if (!userId) return; // Prevent invalid delete requests

    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await deleteUser(userId);
        setUsers(users.filter((user) => user.user_id !== userId));
      } catch (error) {
        console.error("Failed to delete user:", error);
      }
    }
  };

  // ✅ Toggle expanded user details
  const toggleExpand = (userId) => {
    setExpandedUsers((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  // ✅ Filter users based on search, role, and branch
  const filteredUsers = users.filter((user) => {
    return (
      user.username.toLowerCase().includes(searchQuery.toLowerCase()) &&
      (!roleFilter || user.role_id === roleFilter.value) &&
      (!branchFilter || user.branch_id === branchFilter.value)
    );
  });

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold">User Management</h2>
        <button className="bg-blue-600 text-white px-4 py-2 rounded-md flex items-center" 
        onClick={() => setShowModal(true)} >
          <Plus size={16} className="mr-2" /> Add User
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <input
          type="text"
          placeholder="Search Users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="border p-2 rounded-md w-full"
        />

        <Select
          options={roles}
          value={roleFilter}
          onChange={setRoleFilter}
          placeholder="Filter by Role"
          isClearable
        />

        <Select
          options={branches}
          value={branchFilter}
          onChange={setBranchFilter}
          placeholder="Filter by Branch"
          isClearable
        />
      </div>

      {/* User Table */}
      {loading ? (
        <p>Loading users...</p>
      ) : (
        <DataTable
          columns={[
            { key: "username", header: "Username", sortable: true },
            { key: "email", header: "Email", sortable: true },
            {
              key: "role",
              header: "Role",
              sortable: true,
              render: (row) => roles.find((r) => r.value === row.role_id)?.label || "N/A",
            },
            {
              key: "branch",
              header: "Branch",
              sortable: true,
              render: (row) => branches.find((b) => b.value === row.branch_id)?.label || "N/A",
            },
            {
              key: "actions",
              header: "Actions",
              render: (row) => (
                <div className="flex space-x-2">
                  <button className="text-blue-600"><Eye size={16} /></button>
                  <button className="text-green-600"><Edit3 size={16} /></button>
                  <button className="text-red-600" onClick={() => handleDelete(row.user_id)}>
                    <Trash size={16} />
                  </button>
                  <button className="text-gray-600" onClick={() => toggleExpand(row.user_id)}>
                    {expandedUsers[row.user_id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                </div>
              ),
            },
          ]}
          data={filteredUsers}
          expandedRowId={expandedUsers}
          renderExpandedRow={(row) =>
            expandedUsers[row.user_id] && (
              <div className="p-4 bg-gray-100 rounded-lg">
                <h4 className="font-semibold">User Details</h4>
                <p><strong>First Name:</strong> {row.first_name || "N/A"}</p>
                <p><strong>Last Name:</strong> {row.last_name || "N/A"}</p>
                <p><strong>Created At:</strong> {new Date(row.created_at).toLocaleDateString()}</p>
              </div>
            )
          }
        />
      )}
      {/* ✅ Show Modal */}
      {showModal && <AddEditUserModal onClose={() => setShowModal(false)} onSave={() => {}} />}

      
    </div>
  );
}

export default UserManagement;